'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@kiao-voice/ui-kit'
import { fetchInvoiceSettings, saveInvoiceSettings } from '@/features/billing/api-client'
import { readStoredToken } from '@/features/auth/auth-storage'
import {
  EMPTY_INVOICE_FORM,
  InvoiceFields,
  NO_INVOICE_ERRORS,
  hasInvoiceError,
  invoiceFormFromSettings,
  invoiceSettingsFromForm,
  validateInvoiceForm,
  type InvoiceFormErrors,
  type InvoiceFormValue,
} from '@/features/billing/invoice-fields'
import type { InvoiceType } from '@kiao-voice/core'

type LoadStatus = 'loading' | 'ready' | 'load-error'

interface Props {
  /** Account email the personal e-invoice is sent to (from /auth/me). */
  email: string | null
  style?: React.CSSProperties
  /** Reads the stored bearer token; injectable for tests. */
  getTokenFn?: () => string | null
  /** Active-context key — an effect dependency only, so switching workspace
   *  re-loads the (org-scoped) settings. The header itself is attached by the
   *  api-client via `activeContextHeaders()`. */
  activeContext?: string
  /** Injected in tests; default to the real api-client calls. */
  loadFn?: typeof fetchInvoiceSettings
  saveFn?: typeof saveInvoiceSettings
  /** Bump to force a re-load (e.g. after the invoice was edited + saved inside
   *  the checkout modal, which this panel doesn't own). */
  reloadSignal?: number
  /** Render without the panel chrome (heading + bordered card) — used when a
   *  numbered section (05 發票資訊) already provides the title + description. */
  embedded?: boolean
}

/**
 * 發票資訊 panel (design/app-account.html · data-od-id="invoice").
 *
 * The toggle + conditional fields are the shared {@link InvoiceFields} (also used
 * inside the checkout modal); this panel adds the load/save/toast wrapper.
 * Settings are persisted server-side and ORG-SCOPED — switching active context
 * (personal ↔ team) loads that org's own invoice identity. The form hydrates from
 * GET /billing/invoice-settings on mount and PUTs on save; the client-side
 * checksum (in InvoiceFields) stays as instant feedback ahead of the server.
 */
export function InvoicePanel({
  email,
  style,
  getTokenFn = readStoredToken,
  activeContext,
  loadFn = fetchInvoiceSettings,
  saveFn = saveInvoiceSettings,
  reloadSignal,
  embedded = false,
}: Props) {
  const { t } = useTranslation('web')

  const [status, setStatus] = useState<LoadStatus>('loading')
  const [form, setForm] = useState<InvoiceFormValue>(EMPTY_INVOICE_FORM)
  const [errors, setErrors] = useState<InvoiceFormErrors>(NO_INVOICE_ERRORS)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)
  // Store the saved MODE (not a pre-translated string) so the toast re-localizes
  // if the user switches language afterwards.
  const [saved, setSaved] = useState<{ mode: InvoiceType; taxId: string } | null>(null)

  useEffect(() => {
    const token = getTokenFn()
    if (!token) {
      // Logged-out / SSR — nothing to load; show the personal-mode default.
      setStatus('ready')
      return
    }
    let cancelled = false
    setStatus('loading')
    setSaved(null)
    setSaveError(false)
    loadFn()
      .then((resp) => {
        if (cancelled) return
        setForm(invoiceFormFromSettings(resp.invoiceSettings))
        setErrors(NO_INVOICE_ERRORS)
        setStatus('ready')
      })
      .catch((cause) => {
        reportHandledException(cause, { source: 'app/app/account/billing/invoice-panel.tsx' })
        if (cancelled) return
        // Visible recovery (load-error toast) + REPORT so a systematic outage
        // isn't operator-blind (no silent catch).
        console.warn('invoice: failed to load invoice settings', cause)
        setStatus('load-error')
      })
    return () => {
      cancelled = true
    }
    // `activeContext` re-runs the load on a workspace switch (org-scoped settings);
    // `reloadSignal` after an in-checkout save.
  }, [getTokenFn, loadFn, activeContext, reloadSignal])

  // Any edit clears prior validation errors + the saved/error toasts.
  const handleChange = useCallback((next: InvoiceFormValue) => {
    setForm(next)
    setErrors(NO_INVOICE_ERRORS)
    setSaved(null)
    setSaveError(false)
  }, [])

  const handleSave = async () => {
    const token = getTokenFn()
    if (!token) return

    const nextErrors = validateInvoiceForm(form)
    setErrors(nextErrors)
    if (hasInvoiceError(nextErrors)) return

    setSaving(true)
    setSaveError(false)
    setSaved(null)
    try {
      const resp = await saveFn(invoiceSettingsFromForm(form))
      setForm(invoiceFormFromSettings(resp.invoiceSettings))
      setSaved({
        mode: resp.invoiceSettings.invoiceType,
        taxId: resp.invoiceSettings.taxId ?? '',
      })
    } catch (cause) {
      reportHandledException(cause, { source: 'app/app/account/billing/invoice-panel.tsx' })
      // Visible recovery: surface a localized error toast; the saved confirmation
      // never shows, so the user knows it didn't persist. REPORT too (no silent
      // catch) so the real cause is visible to operators.
      console.warn('invoice: failed to save invoice settings', cause)
      setSaveError(true)
    } finally {
      setSaving(false)
    }
  }

  const body = (
    <>
      {status === 'loading' && (
        <p className="note" data-testid="invoice-loading">
          {t('billing.invoiceLoading')}
        </p>
      )}

      {status === 'load-error' && (
        <p className="toast show" data-testid="invoice-load-error">
          {t('billing.invoiceLoadError')}
        </p>
      )}

      {status === 'ready' && (
        <>
          <div style={{ marginTop: 'var(--gap-sm)' }}>
            <InvoiceFields
              value={form}
              errors={errors}
              email={email}
              disabled={saving}
              onChange={handleChange}
            />
          </div>

          <div style={{ marginTop: 'var(--gap-md)' }}>
            <Button
              variant="secondary"
              size="sm"
              data-testid="invoice-save"
              onClick={handleSave}
              disabled={saving}
            >
              {t('billing.invoiceSave')}
            </Button>
          </div>
          {saved && (
            <p className="toast show" data-testid="invoice-toast">
              {saved.mode === 'company'
                ? t('billing.invoiceSavedCompany', { taxId: saved.taxId })
                : t('billing.invoiceSavedPersonal')}
            </p>
          )}
          {saveError && (
            <p className="toast show" data-testid="invoice-save-error">
              {t('billing.invoiceSaveError')}
            </p>
          )}
        </>
      )}
    </>
  )

  // Embedded in a numbered section (05) — the section owns the title + desc, so
  // render the fields directly without the panel chrome.
  if (embedded) {
    return (
      <div data-testid="invoice-panel" style={style}>
        {body}
      </div>
    )
  }

  return (
    <div className="panel panel-plain" style={style} data-testid="invoice-panel">
      <h3>{t('billing.invoiceHeading')}</h3>
      <p className="p-desc">{t('billing.invoiceDesc')}</p>
      {body}
    </div>
  )
}
